import { getStoredSessionToken } from "./appleAuth";
import type { PackStatus, ReadingPackSummaryDto } from "./types";

export const PUBLISH_PACKS_PER_INVOKE = 12;

export type ReviewQueueResponse = {
  month: string;
  statusFilter: "pending" | "draft" | "reviewed" | "published";
  packs: ReadingPackSummaryDto[];
  warnings: string[];
  configured: boolean;
  environment: string;
  packsPerInvoke: number;
};

export type PublishPackResult = {
  recordName: string;
  dateKey: string;
  languageCode: string;
  ok: boolean;
  previousStatus?: PackStatus;
  status?: PackStatus;
  error?: string;
};

export type PublishBatchResult = {
  action: "review" | "publish" | "unpublish";
  attempted: number;
  succeeded: number;
  failed: number;
  results: PublishPackResult[];
  errorSummary: string | null;
};

export type PublishProgress = {
  chunkIndex: number;
  chunkTotal: number;
};

async function authHeader(): Promise<HeadersInit> {
  const token = getStoredSessionToken();
  if (!token) throw new Error("Sign in required.");
  return { Authorization: `Bearer ${token}` };
}

async function readJson<T>(res: Response): Promise<T> {
  const data = (await res.json()) as T & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Request failed (${res.status})`);
  return data;
}

export async function fetchReviewQueue(
  month: string,
  status: ReviewQueueResponse["statusFilter"] = "pending",
): Promise<ReviewQueueResponse> {
  const headers = new Headers(await authHeader());
  const qs = new URLSearchParams({ month, status });
  const res = await fetch(`/api/publish/queue?${qs}`, { headers });
  return readJson(res);
}

function chunkNames(names: string[], size = PUBLISH_PACKS_PER_INVOKE): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < names.length; i += size) {
    chunks.push(names.slice(i, i + size));
  }
  return chunks;
}

async function runActionChunked(
  action: "review" | "publish" | "unpublish",
  recordNames: string[],
  onProgress?: (progress: PublishProgress) => void,
): Promise<PublishBatchResult> {
  const unique = [...new Set(recordNames.map((n) => n.trim()).filter(Boolean))];
  if (unique.length === 0) throw new Error("Select at least one pack.");

  const chunks = chunkNames(unique);
  const merged: PublishBatchResult = {
    action,
    attempted: 0,
    succeeded: 0,
    failed: 0,
    results: [],
    errorSummary: null,
  };

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i]!;
    onProgress?.({ chunkIndex: i + 1, chunkTotal: chunks.length });
    const headers = new Headers(await authHeader());
    headers.set("Content-Type", "application/json");
    const res = await fetch(`/api/publish/${action}`, {
      method: "POST",
      headers,
      body: JSON.stringify({ recordNames: chunk }),
    });
    const part = await readJson<PublishBatchResult>(res);
    merged.attempted += part.attempted;
    merged.succeeded += part.succeeded;
    merged.failed += part.failed;
    merged.results.push(...part.results);
  }

  merged.errorSummary =
    merged.failed > 0 ? `${merged.failed} of ${merged.attempted} pack(s) failed.` : null;
  return merged;
}

export async function reviewPacks(
  recordNames: string[],
  onProgress?: (progress: PublishProgress) => void,
): Promise<PublishBatchResult> {
  return runActionChunked("review", recordNames, onProgress);
}

export async function publishPacks(
  recordNames: string[],
  onProgress?: (progress: PublishProgress) => void,
): Promise<PublishBatchResult> {
  return runActionChunked("publish", recordNames, onProgress);
}

export async function unpublishPacks(
  recordNames: string[],
  onProgress?: (progress: PublishProgress) => void,
): Promise<PublishBatchResult> {
  return runActionChunked("unpublish", recordNames, onProgress);
}
